import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import { useFieldArray, useForm } from "react-hook-form";
import { queryClient } from "../../router/MerchantInvoiceRouter";
import APIS from "../../constant/Endpoint";
import { paymentValidationForm } from "../../utils/validationSchemas";
import Button from "../Reusable/Button";
import usePostHook from "../../customHooks/usePostHook";
import { useParams } from "react-router";
import ComboboxInput from "../Reusable/ComboboxInput";
import { useState } from "react";
import NumberField from "../Reusable/NumberField";
import { PlusIcon, MinusIcon } from "@heroicons/react/24/outline";
import { tw } from "@careinfo/tailwind";
import { toast } from "react-toastify";

const MakePayment = (props: any) => {
  const { payableAmount } = props;
  const { id } = useParams();
  const { mutateAsync, isPostLoading } = usePostHook({
    queryKey: ["merchant-invoice", `merchant-invoice-${id}`],
    navigateURL: "",
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    setValue,
    reset,
    getValues,
    clearErrors,
    control,
  } = useForm<any>({
    resolver: yupResolver(paymentValidationForm),
    defaultValues: {
      payments: [{ payment_method: "", amount: 0 }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "payments",
  });

  const [user, setUser] = useState();

  const handleMakePayment = async (data: any) => {
    const totalAmount = data.payments.reduce(
      (acc: number, curr: any) => acc + Number(curr.amount || 0),
      0
    );
    if (totalAmount !== payableAmount) {
      if (totalAmount === -payableAmount) {
        const url = `${APIS.MAKE_PAYMENT}`;
        const formData = { ...data, invoice_id: id };
        try {
          const res = await mutateAsync({ url, formData, setError });
          if (res.status === 200) {
            props?.setMakePayment(false);
            reset();
            queryClient.refetchQueries();
            props?.onPaymentComplete?.(formData);
          }
        } catch (err) {
          console.log(err);
        }
      } else {
        toast.error(
          `Total payment amount (Rs.${totalAmount}) must equal payable amount Rs.${payableAmount} or Rs.${-payableAmount}`
        );
      }
      return;
    }

    const url = `${APIS.MAKE_PAYMENT}`;
    const formData = { ...data, invoice_id: id };
    try {
      const res = await mutateAsync({ url, formData, setError });
      if (res.status === 200) {
        props?.setMakePayment(false);
        reset();
        queryClient.refetchQueries();
        props?.onPaymentComplete?.(formData);
      }
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <section className={tw`bg-white border mt-4 rounded-lg`}>
      <div className={tw`max-w-4xl mx-auto px-4`}>
        <form
          className={tw`mb-6 w-full`}
          onSubmit={handleSubmit((data: any) => {
            handleMakePayment(data);
          })}
        >
          {fields.map((item, index) => (
            <div key={item.id} className="flex flex-row items-end gap-4">
              <div className={tw`w-[500px]`}>
                <ComboboxInput
                  url={APIS.PAYMENT_METHOD}
                  props={{}}
                  queryKey={"payment_method"}
                  register={register(`payments.${index}.payment_method`)}
                  errors={errors?.payments?.[index]?.payment_method}
                  name={`payments.${index}.payment_method`}
                  label={"Payment Method"}
                  setValue={setValue}
                  placeholder={"Search Payment Method"}
                  getValues={getValues}
                  clearErrors={clearErrors}
                  setUser={setUser}
                />
              </div>
              <div className={tw`w-1/4`}>
                <NumberField
                  value={getValues(`payments.${index}.amount`)}
                  register={register(`payments.${index}.amount`)}
                  errors={errors?.payments?.[index]?.amount}
                  label={"Amount"}
                  setValue={setValue}
                  clearErrors={clearErrors}
                />
              </div>

              <div className={tw`flex items-center justify-center gap-2 mt-8`}>
                <button
                  type="button"
                  onClick={() => append({ payment_method: "", amount: 0 })}
                  className={tw`p-2 bg-green-100 border hover:bg-green-200 border-green-400 rounded-lg focus:outline-none`}
                >
                  <PlusIcon className={tw`w-5 h-5 text-green-600`} />
                </button>
                {fields.length > 1 && (
                  <button
                    type="button"
                    onClick={() => remove(index)}
                    className={tw`p-2 bg-red-100 hover:bg-red-200 rounded-lg`}
                  >
                    <MinusIcon className={tw`w-5 h-5 text-red-600`} />
                  </button>
                )}
              </div>
            </div>
          ))}
          <div>
            {(() => {
              const total = (getValues("payments") || []).reduce(
                (acc: number, p: any) => acc + Number(p.amount || 0),
                0
              );
              // Make payableAmount positive (shadowing outer prop with a local positive value)
              const payableAmount = Math.abs(Number(props.payableAmount || 0));
              return (
                <div
                  className={tw`flex items-center justify-between py-2 mt-4`}
                >
                  <div>
                    <p className={tw`text-blue-600 text-xs font-semibold`}>
                      Total entered amount: Rs.{total}
                    </p>
                  </div>
                  <div>
                    {total < payableAmount && (
                      <p className={tw`text-yellow-600 text-xs font-semibold`}>
                        Remaining payable amount: Rs.{payableAmount - total}
                      </p>
                    )}
                    {total === payableAmount && (
                      <p className={tw`text-green-600 text-xs font-semibold`}>
                        Total amount matched.
                      </p>
                    )}
                    {total > payableAmount && (
                      <p className={tw`text-red-600 text-xs font-semibold`}>
                        Overpaid by: Rs.{total - payableAmount}
                      </p>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>

          <div
            className={tw`my-4 px-4 mb-4 bg-white rounded-lg border border-gray-200`}
          >
            <label htmlFor="comment" className={tw`sr-only`}>
              Your comment
            </label>
            <textarea
              id="comment"
              rows={6}
              {...register("remarks")}
              className={tw`px-0 w-full text-sm text-gray-900 border-0 focus:ring-0 focus:outline-none`}
              placeholder="Write a comment..."
              required
            ></textarea>
          </div>

          <div className={tw`flex justify-end w-full gap-4`}>
            <div className={tw`w-32`}>
              <Button
                type={"button"}
                label={"Cancel"}
                color="bg-gray-500"
                onClick={() => props.setMakePayment(false)}
              />
            </div>
            <div className={tw`w-32`}>
              <Button
                label={"Submit"}
                isLoading={isPostLoading}
                // onClick={handleSubmit((data: any) => handleMakePayment(data))}
              />
            </div>
          </div>
        </form>
      </div>
    </section>
  );
};

export default MakePayment;
